/**
 * Form submission interface.
 *
 * The frontend ships before any backend exists, so forms talk to this narrow
 * interface rather than to a database client. The active implementation simply
 * reports that no backend is connected, and the UI explains that to the
 * visitor instead of failing.
 *
 * To connect a backend (see `docs/BACKEND_PLAN.md`): add an adapter that
 * implements `SubmissionAdapter` and change the `adapter` binding below. No
 * form component needs to change.
 */

import type { Locale } from '@/i18n'

export type RegistrationRole = 'participant' | 'volunteer' | 'researcher' | 'partner' | 'media'

export interface RegistrationInput {
  fullName: string
  email: string
  organization: string | null
  role: RegistrationRole
  preferredSiteId: string | null
  message: string | null
  locale: Locale
}

export interface NewsletterInput {
  email: string
  locale: Locale
  source: string
}

export type SubmissionResult =
  | { ok: true }
  | { ok: false; reason: 'not-configured' | 'failed'; detail?: string }

export interface SubmissionAdapter {
  /** False while no backend is wired up; forms use this to warn up front. */
  readonly isConnected: boolean
  submitRegistration(input: RegistrationInput): Promise<SubmissionResult>
  subscribeToNewsletter(input: NewsletterInput): Promise<SubmissionResult>
}

/** Placeholder adapter: accepts nothing, explains why. */
const notConnectedAdapter: SubmissionAdapter = {
  isConnected: false,
  async submitRegistration() {
    return { ok: false, reason: 'not-configured' }
  },
  async subscribeToNewsletter() {
    return { ok: false, reason: 'not-configured' }
  },
}

const adapter: SubmissionAdapter = notConnectedAdapter

export const isBackendConnected = adapter.isConnected

export function submitRegistration(input: RegistrationInput): Promise<SubmissionResult> {
  return adapter.submitRegistration(input)
}

export function subscribeToNewsletter(input: NewsletterInput): Promise<SubmissionResult> {
  return adapter.subscribeToNewsletter(input)
}
